import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Avatar } from "./ChatList";

function Pickable({ items, picked, toggle, testid }) {
  return (
    <div className="thin-scroll max-h-72 space-y-0.5 overflow-y-auto">
      {items.map((c) => (
        <label key={c.key} data-testid={`${testid}-${c.key.replace(/\W+/g, "-").toLowerCase()}`} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-zinc-50">
          <input type="checkbox" checked={picked.includes(c.key)} onChange={() => toggle(c.key)} className="h-4 w-4 accent-[#A9792C]" />
          <Avatar name={c.name} type={c.type} size="h-8 w-8" /><span className="truncate text-sm">{c.name}</span>
        </label>
      ))}
    </div>
  );
}

const useToggle = (init = []) => {
  const [picked, setPicked] = useState(init);
  return [picked, (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k])), setPicked];
};

export function GroupDialog({ open, onOpenChange, users, group, onSave, onLeave }) {
  const [name, setName] = useState("");
  const [picked, toggle, setPicked] = useToggle();
  useEffect(() => { if (open) { setName(group?.name || ""); setPicked(group?.members || []); } }, [open, group, setPicked]);
  const people = users.map((u) => ({ key: u.id, name: u.name, type: "dm" }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="chat-group-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>{group ? "Group info" : "New group"}</DialogTitle></DialogHeader>
        <input data-testid="chat-group-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" className="h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm outline-none focus:border-[#A9792C]" />
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Members · {picked.length} selected</div>
        <Pickable items={people} picked={picked} toggle={toggle} testid="chat-group-member" />
        <div className="flex justify-between gap-2">
          {group ? <Button data-testid="chat-group-leave" variant="ghost" className="text-rose-600" onClick={() => window.confirm("Leave this group?") && onLeave()}>Exit group</Button> : <span />}
          <Button data-testid="chat-group-save" disabled={!name.trim() || !picked.length} onClick={() => onSave(name, picked)}>{group ? "Save" : "Create group"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ForwardDialog({ msg, onClose, convs, onForward }) {
  const [picked, toggle, setPicked] = useToggle();
  const [q, setQ] = useState("");
  useEffect(() => { setPicked([]); setQ(""); }, [msg, setPicked]);
  return (
    <Dialog open={!!msg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="chat-forward-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Forward message to</DialogTitle></DialogHeader>
        <input data-testid="chat-forward-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="h-9 w-full rounded-lg bg-zinc-100 px-3 text-sm outline-none" />
        <Pickable items={convs.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))} picked={picked} toggle={toggle} testid="chat-forward-to" />
        <Button data-testid="chat-forward-send" disabled={!picked.length} onClick={() => onForward(picked)}>Forward to {picked.length || ""} chat{picked.length === 1 ? "" : "s"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function StarredDialog({ open, onOpenChange, nameOf, onOpen }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) api.get("/chat/starred").then((r) => setRows(r.data)).catch(() => setRows([])); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="chat-starred-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Star className="h-4 w-4 fill-[#C9A24A] text-[#C9A24A]" />Starred messages</DialogTitle></DialogHeader>
        <div className="thin-scroll max-h-[60vh] space-y-2 overflow-y-auto">
          {rows.length ? rows.map((m, i) => (
            <button key={m.id} data-testid={`chat-starred-${i}`} onClick={() => onOpen(m)} className="block w-full rounded-lg border border-zinc-100 p-3 text-left transition-colors hover:bg-zinc-50">
              <div className="flex justify-between text-xs text-zinc-500"><span><b className="text-zinc-800">{m.author}</b> · {nameOf(m.channel)}</span><span>{new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span></div>
              <div className="mt-1 line-clamp-3 text-sm text-zinc-700">{m.text || m.attachment?.name}</div>
            </button>
          )) : <div className="py-8 text-center text-sm text-zinc-400">No starred messages yet. Star a message from its menu to find it here.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
